/**
 * NobleButton - Semi-Neumorphic Button Component
 * 
 * Industrial-grade button with physical press effect
 * Features:
 * - Semi-neumorphic style with metal texture
 * - Physical press animation (shadow invert on click)
 * - Lift effect on hover
 * - Minimum 48px touch target for glove-friendly operation
 * 
 * @version 1.0.0
 * @date December 1, 2025
 */

import React from 'react';
import { motion } from 'framer-motion';
import styled from '@emotion/styled';
import { NobleTokens } from '../../../theme/noble-tokens';

export interface NobleButtonProps {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  style?: React.CSSProperties;
}

const StyledMotionButton = styled(motion.button)<{
  $variant: 'primary' | 'secondary' | 'danger' | 'ghost';
  $size: 'small' | 'medium' | 'large';
  $disabled?: boolean;
}>`
  /* Base Styles */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${NobleTokens.spacing.xs};
  
  font-family: ${NobleTokens.typography.fontFamily.primary};
  font-size: ${NobleTokens.typography.fontSize.body};
  font-weight: ${NobleTokens.typography.fontWeight.semibold};
  line-height: ${NobleTokens.typography.lineHeight.normal};
  
  border: none;
  border-radius: ${NobleTokens.borderRadius.md};
  cursor: pointer;
  transition: all ${NobleTokens.animation.duration.fast} ${NobleTokens.animation.easing.easeOut};
  
  /* Size Variants */
  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return `
          height: ${NobleTokens.components.button.height.small};
          padding: ${NobleTokens.components.button.padding.small};
        `;
      case 'large':
        return `
          height: ${NobleTokens.components.button.height.large};
          padding: ${NobleTokens.components.button.padding.large};
        `;
      default:
        return `
          height: ${NobleTokens.components.button.height.medium};
          padding: ${NobleTokens.components.button.padding.medium};
        `;
    }
  }}
  
  /* Variant Styles */
  ${({ $variant, $disabled }) => {
    if ($disabled) {
      return `
        opacity: 0.5;
        cursor: not-allowed;
        background: ${NobleTokens.colors.accent.steelGrayLight};
        color: ${NobleTokens.colors.text.disabled};
        box-shadow: none;
      `;
    }
    
    switch ($variant) {
      case 'primary':
        return `
          background: ${NobleTokens.colors.gradients.command};
          color: white;
          box-shadow: ${NobleTokens.shadows.level1}, inset 0 -2px 4px rgba(0, 0, 0, 0.1);
          
          &:hover:not(:disabled) {
            box-shadow: ${NobleTokens.shadows.buttonHover};
          }
          
          &:active:not(:disabled) {
            box-shadow: ${NobleTokens.shadows.buttonActive};
          }
        `;
      
      case 'danger':
        return `
          background: ${NobleTokens.colors.semantic.criticalRed};
          color: white;
          box-shadow: 0 4px 12px rgba(230, 57, 70, 0.25), inset 0 -2px 4px rgba(0, 0, 0, 0.1);
          
          &:hover:not(:disabled) {
            background: ${NobleTokens.colors.semantic.criticalRedLight};
            box-shadow: 0 6px 16px rgba(230, 57, 70, 0.35), inset 0 -2px 4px rgba(0, 0, 0, 0.1);
          }
          
          &:active:not(:disabled) {
            background: ${NobleTokens.colors.semantic.criticalRedDark};
            box-shadow: 0 2px 6px rgba(230, 57, 70, 0.25), inset 0 2px 4px rgba(0, 0, 0, 0.15);
          }
        `;
      
      case 'secondary':
        return `
          background: transparent;
          color: ${NobleTokens.colors.accent.steelGray};
          border: 2px solid ${NobleTokens.colors.accent.steelGray};
          box-shadow: none;
          
          &:hover:not(:disabled) {
            background: ${NobleTokens.colors.accent.steelGray};
            color: white;
          }
          
          &:active:not(:disabled) {
            background: ${NobleTokens.colors.accent.steelGrayDark};
          }
        `;
      
      case 'ghost':
        return `
          background: transparent;
          color: ${NobleTokens.colors.primary.commandBlue};
          box-shadow: none;
          
          &:hover:not(:disabled) {
            background: rgba(20, 90, 84, 0.08);
          }
          
          &:active:not(:disabled) {
            background: rgba(20, 90, 84, 0.15);
          }
        `;
      
      default:
        return '';
    }
  }}
`;

/**
 * NobleButton Component
 */
export const NobleButton: React.FC<NobleButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  disabled = false,
  children,
  onClick,
  icon,
  loading,
  ...rest
}) => {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) return;
    onClick?.(e as any);
  };

  return (
    <StyledMotionButton
      $variant={variant}
      $size={size}
      $disabled={disabled || loading}
      disabled={disabled || loading}
      onClick={handleClick}
      whileHover={
        !disabled && !loading
          ? {
              y: -2,
              transition: { duration: 0.15 },
            }
          : {}
      }
      whileTap={
        !disabled && !loading
          ? {
              y: 1,
              transition: { duration: 0.1 },
            }
          : {}
      }
      {...rest}
    >
      {loading && (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ animation: 'spin 1s linear infinite' }}
        >
          <circle
            cx="8"
            cy="8"
            r="6"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray="31.4 31.4"
            strokeLinecap="round"
            opacity="0.5"
          />
        </svg>
      )}
      {!loading && icon && <span>{icon}</span>}
      {children}
    </StyledMotionButton>
  );
};

// Add spin animation for loading spinner
const style = document.createElement('style');
style.innerHTML = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

export default NobleButton;

